import type { ModelCommentThread } from '../domain';
import type { Position } from '@view-model/models/common/types/ui';
import { ModelCommentThreadDeleteCommand } from '../command';
import type { CommandManager } from '@model-framework/command';
import type { IPositionSetRepository } from '@view-model/models/common/PositionSet';
import { PositionSetRepository } from '@view-model/models/common/PositionSet/infrastructure/PositionSetRepository';
import { ModelCommentThreadRepository } from '@view-model/models/sticky/ModelComment';
import type {
  ModelCommentId,
  ModelCommentThreadId,
  ModelId,
  ViewModelId,
  ModelCommentJSON,
  UserId,
} from 'balus-common';
import { RTDBPath } from 'balus-common';

export class ModelCommentThreadOperation {
  private readonly threadRepository: ModelCommentThreadRepository;
  private readonly positionRepository: IPositionSetRepository;

  constructor(
    viewModelId: ViewModelId,
    modelId: ModelId,
    private readonly threadId: ModelCommentThreadId,
    private readonly commandManager: CommandManager
  ) {
    this.threadRepository = new ModelCommentThreadRepository(viewModelId, modelId);

    this.positionRepository = new PositionSetRepository(RTDBPath.Comment.positionsPath(viewModelId, modelId));
  }

  private async fetchThread(): Promise<ModelCommentThread | undefined> {
    return this.threadRepository.getOne(this.threadId);
  }

  private async fetchPosition(): Promise<Position | undefined> {
    const position = await this.positionRepository.getMany([this.threadId]);
    return position.find(this.threadId);
  }

  async deleteThread(): Promise<void> {
    const thread = await this.fetchThread();
    const position = await this.fetchPosition();
    if (!thread || !position) return;

    const command = new ModelCommentThreadDeleteCommand(
      thread,
      position,
      this.threadRepository,
      this.positionRepository
    );
    this.commandManager.execute(command);
  }

  async resolve(userId: UserId): Promise<void> {
    const thread = await this.fetchThread();
    if (!thread) return;

    await this.threadRepository.saveOne(thread.withStatus('Resolved'));
    await this.saveReplyComment('> 解決済みです | Resolved', userId, 'System');
  }

  async reopen(userId: UserId): Promise<void> {
    const thread = await this.fetchThread();
    if (!thread) return;

    await this.threadRepository.saveOne(thread.withStatus('Open'));
    await this.saveReplyComment('> 再開しました | Resumed', userId, 'System');
  }

  async addReplyComment(body: string, userId: UserId): Promise<void> {
    await this.saveReplyComment(body, userId, 'Regular');
  }

  private async saveReplyComment(body: string, userId: UserId, type: ModelCommentJSON['type']): Promise<void> {
    const thread = await this.fetchThread();
    if (!thread) return;

    await this.threadRepository.saveOne(thread.addReplyComment(userId, body, type));
  }

  async updateCommentBody(commentId: ModelCommentId, body: string): Promise<void> {
    const thread = await this.fetchThread();
    if (!thread) return;

    await this.threadRepository.saveOne(thread.withCommentBody(commentId, body));
  }

  async addReadByUserId(userId: UserId): Promise<void> {
    const thread = await this.fetchThread();
    if (!thread) return;

    const updated = thread.setReadByUserId(userId);
    if (updated === thread) return;

    await this.threadRepository.saveOne(updated);
  }

  async deleteComment(commentId: ModelCommentId): Promise<void> {
    const thread = await this.fetchThread();
    if (!thread) return;

    await this.threadRepository.saveOne(thread.removeReplyComment(commentId));
  }
}
